/**
 * The entry screen once the session is over and the player has left it — the
 * session's-end notice's Exit, Exit or Disconnect in the end lobby, or a
 * reload of a link whose end the tab remembers (domains/session-ended.ts). It
 * joins nothing: every other way in starts a join of its own, and a student's
 * link would join the ended session again and be refused.
 *
 * The notice the player has just left, standing on the entry: the session's
 * title as the headline and the sentence naming its world as the meaning; on
 * the card the plane mark in the accent beside the thanks, and what is left to
 * do — close the tab — as a caption. No button: there is nothing more to do
 * here.
 */

import { Trans, useTranslation } from 'react-i18next';

import { useSessionEndedStore } from '../../domains/session-ended';
import { Emphasis } from '../ui/page-header';
import { PLANE_MARK } from '../common/plane-glyph';
import { EntryCaption, EntryCard, EntryHead } from './entry-card';
import { EntryFrame } from './entry-frame';

/** The world's name in the sentence: held on one line, in the sentence's own ink. */
const WORLD = { b: <Emphasis tone="plain" /> };

export function SessionEndedEntry({ search }: { search: string }) {
  const { t } = useTranslation();
  const world = useSessionEndedStore((s) => s.worldName);

  return (
    <EntryFrame
      search={search}
      title={t('sessionEnded.title')}
      meaning={
        world ? (
          <Trans
            i18nKey="sessionEnded.message"
            values={{ world }}
            components={WORLD}
            shouldUnescape
            tOptions={{ interpolation: { escapeValue: true } }}
          />
        ) : undefined
      }
    >
      <EntryCard>
        <EntryHead glyph={PLANE_MARK} tone="accent">
          {t('sessionEnded.thankYou')}
        </EntryHead>
        <EntryCaption>{t('entry.closeTab')}</EntryCaption>
      </EntryCard>
    </EntryFrame>
  );
}
